import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  File,
  Download,
  Eye,
  MapPin,
  Clock,
  Thermometer,
  ShieldAlert,
  AlertTriangle,
  Globe,
  Home,
  Ship,
  Plane,
  Plus,
  Trash2,
  Lock,
  ExternalLink,
} from "lucide-react";
import {
  Shipment,
  DriverAttachment,
  AttachmentMeta,
  Country,
} from "../../types";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  downloadShipmentAttachment,
  getShipment,
} from "../../store/slices/shipmentSlice";
import { getServices } from "../../store/slices/servicesSlice";
import { shipmentAPI, invoiceAPI } from "../../services/api";
import { ServiceAddInline } from "../ShipmentForm/ServiceAddInline";
import { STATUS_STEPS } from "../../constants/formOptions";
import { displayCargoNumber } from "../../utils/cargoDisplay";
import {
  BL_TYPE_OPTIONS,
  CONTAINER_TYPE_OPTIONS,
  BL_REMARK_LOCATION_OPTIONS,
} from "../../constants/shipmentFormConstants";
import { format } from "date-fns";
import CMRSharingModal from "../modals/CMRSharingModal";
import { CommentSection } from "../comment/CommentSection";
import ReclamationSection from "./ReclamationSection";
import InvoiceRefs from "../invoices/InvoiceRefs";
import POHStatusDots from "../poh/POHStatusDots";
import { listHasTax, sumTax, taxCellLabel } from "../../utils/tax";
import { vehicleRefLabel } from "../../utils/vehicleLabel";

interface ShipmentReadonlyProps {
  shipment: Shipment;
}

const ShipmentReadonly: React.FC<ShipmentReadonlyProps> = ({ shipment }) => {
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isCostServicesOpen, setIsCostServicesOpen] = useState(false);
  const [isStatusHovered, setIsStatusHovered] = useState(false);
  const [sharingDocumentId, setSharingDocumentId] = useState<Shipment | null>(
    null,
  );
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("shipments");

  // Invoiced service rows only store the invoice _id; resolve the human invoice
  // number (for display + navigation) from the shipment's linked invoices.
  const [invoiceNumberById, setInvoiceNumberById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const hasInvoiced = (shipment.services ?? []).some((s) => s.invoiceId);
    if (!shipment._id || !hasInvoiced) return;
    let alive = true;
    invoiceAPI
      .list({ shipmentId: shipment._id, limit: 50 })
      .then((res) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        (
          (res.data?.data?.invoices ?? []) as Array<{
            _id?: string;
            invoiceNumber?: string;
          }>
        ).forEach((inv) => {
          if (inv?._id) map[inv._id] = inv.invoiceNumber ?? "";
        });
        setInvoiceNumberById(map);
      })
      .catch(() => {
        if (alive) setInvoiceNumberById({});
      });
    return () => {
      alive = false;
    };
  }, [shipment._id, shipment.services]);

  // ── Post-completion service management ────────────────────────────────────
  // Completed/locked shipments can never be unlocked for editing, but billable
  // extras still need to be added (and uninvoiced rows removed) from the
  // system side via the dedicated service endpoints. Rows already on a locked
  // invoice are immutable.
  const canManageServices =
    shipment.status === "Completed" || !!shipment.driverAccess?.locked;
  const [showAddService, setShowAddService] = useState(false);
  const [serviceBusy, setServiceBusy] = useState(false);
  const { isLoaded: servicesLoaded, services: serviceCatalog } =
    useAppSelector((state) => state.services);

  useEffect(() => {
    if (canManageServices && !servicesLoaded) {
      dispatch(getServices({ search: "", page: 1, limit: 1000 }));
    }
  }, [canManageServices, servicesLoaded, dispatch]);

  const handleAddService = async (payload: Record<string, unknown>) => {
    setServiceBusy(true);
    try {
      await shipmentAPI.addShipmentService(shipment._id, payload);
      toast.success(t("services.addedSuccess"));
      setShowAddService(false);
      await dispatch(getShipment(shipment._id));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("services.addFailed"));
    } finally {
      setServiceBusy(false);
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!window.confirm(t("services.confirmDelete"))) return;
    setServiceBusy(true);
    try {
      await shipmentAPI.deleteShipmentService(shipment._id, serviceId);
      toast.success(t("services.deletedSuccess"));
      await dispatch(getShipment(shipment._id));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("services.deleteFailed"));
    } finally {
      setServiceBusy(false);
    }
  };

  // Transport-mode-aware display: legacy shipments without the field are ROAD.
  const transportMode = shipment.transportMode || "ROAD";
  const isRoad = transportMode === "ROAD";
  const isAir = transportMode === "AIR";
  const isSea = transportMode === "SEA";

  const blTypeLabel =
    BL_TYPE_OPTIONS.find((o) => o.value === shipment.seaDetails?.blType)
      ?.label || "-";
  const containerTypeLabel =
    CONTAINER_TYPE_OPTIONS.find(
      (o) => o.value === shipment.seaDetails?.containerType,
    )?.label || "-";
  // Place of Delivery (HBL): use the explicit sea field when filled, otherwise
  // fall back to the delivery block address.
  const deliveryAddressString = [
    shipment?.delivery?.address?.city,
    shipment?.delivery?.address?.country &&
    typeof shipment.delivery.address.country === "object"
      ? shipment.delivery.address.country?.name
      : shipment?.delivery?.address?.country,
  ]
    .filter((p): p is string => !!p && String(p).trim() !== "")
    .join(", ");
  const placeOfDeliveryDisplay =
    shipment.seaDetails?.placeOfDelivery || deliveryAddressString || "-";
  // Stored loadType values never change; only the label per mode does:
  // Full/Partial (ROAD) -> FCL/LCL (SEA) -> FTL/LTL (AIR).
  const loadTypeLabel = shipment.loadType
    ? transportMode === "SEA"
      ? shipment.loadType === "Full Load"
        ? "FCL"
        : "LCL"
      : transportMode === "AIR"
        ? shipment.loadType === "Full Load"
          ? "FTL"
          : "LTL"
        : shipment.loadType
    : "";

  /* ─────────────────────────── helpers ─────────────────────────────────── */

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const downloadAttachment = async (attachment: any) => {
    try {
      const response = await fetch(attachment.fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (err) {}
  };

  const handleDownloadShipmentAttachment = async (
    attachmentId: string,
    previewOnly = false,
  ) => {
    if (!shipment?._id || !attachmentId) return;
    try {
      await dispatch(
        downloadShipmentAttachment({
          shipmentId: shipment._id,
          attachmentId,
          previewOnly,
        }),
      ).unwrap();
    } catch (err: any) {}
  };

  const openAttachment = (attachment: DriverAttachment | AttachmentMeta) => {
    window.open(attachment.fileUrl, "_blank");
  };

  const formatAddress = (
    street?: string,
    city?: string,
    county?: string,
    postalCode?: string,
    country?: Country | string,
  ): string => {
    const countryName = typeof country === "string" ? country : country?.name;
    const parts = [street, city, county, postalCode, countryName].filter(
      (part): part is string => !!part && part.trim() !== "",
    );
    return parts.length > 0 ? parts.join(", ") : t("readonly.notSpecified");
  };

  const stopPropagation = (handler: any) => (e: any) => {
    e.stopPropagation();
    handler(e);
  };

  const handleShareShipment = (shipment: Shipment) => {
    setSharingDocumentId(shipment);
    setIsSharingModalOpen(true);
  };

  const handleCloseSharingModal = () => {
    setIsSharingModalOpen(false);
    setSharingDocumentId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft":
        return "bg-gray-100 text-gray-800";
      case "ReadyForPickup":
        return "bg-purple-100 text-purple-700";
      case "AwaitingPickup":
        return "bg-orange-100 text-orange-800";
      case "InTransit":
        return "bg-blue-100 text-blue-800";
      case "Completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCurrentStepIndex = () =>
    STATUS_STEPS.findIndex((step) => step.key === shipment?.status);

  function safeDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatDate(value: any) {
    const d = safeDate(value);
    return d ? d.toLocaleDateString("en-US") : "-";
  }

  const getStepStatus = useCallback(
    (stepKey: string): string | null => {
      if (!shipment) return null;
      const da = shipment.driverAccess || {};
      const eta = `ETA: ${formatDate(shipment?.delivery?.date)} ${shipment?.delivery?.time || "-"}`;
      const shipmentCreationDate = shipment.shipmentDate;
      const formatTime = (time?: string) =>
        time ? format(new Date(time), "d/M/yyyy  HH:mm") : null;
      const isPresent = (v: any) =>
        v !== undefined &&
        v !== null &&
        (typeof v === "boolean" ? v === true : String(v).trim() !== "");

      const reached = {
        Order: true,
        AwaitingPickup:
          isPresent(da.awaitingPickup) ||
          isPresent(da.awaitingPickupTime) ||
          isPresent(da.journeyStarted) ||
          isPresent(da.journeyStartTime) ||
          isPresent(da.journeyCompleted) ||
          isPresent(da.journeyCompletedTime),
        InTransit:
          isPresent(da.journeyStarted) ||
          isPresent(da.journeyStartTime) ||
          isPresent(da.journeyCompleted) ||
          isPresent(da.journeyCompletedTime),
        Completed:
          isPresent(da.journeyCompleted) || isPresent(da.journeyCompletedTime),
      };

      const stepOrder = [
        "Order",
        "ReadyForPickup",
        "AwaitingPickup",
        "InTransit",
        "Completed",
      ];
      const stepIndex = stepOrder.indexOf(stepKey);
      if (stepIndex === -1) return "Pending";

      let furthest = 0;
      for (let i = stepOrder.length - 1; i >= 0; i--) {
        if (reached[stepOrder[i] as keyof typeof reached]) {
          furthest = i;
          break;
        }
      }

      const timeFor = (key: string) => {
        if (key === "Order" && shipmentCreationDate)
          return formatTime(shipmentCreationDate);
        if (key === "AwaitingPickup" && da.awaitingPickupTime)
          return formatTime(da.awaitingPickupTime);
        if (key === "InTransit" && da.journeyStartTime)
          return formatTime(da.journeyStartTime);
        if (key === "Completed" && da.journeyCompletedTime)
          return formatTime(da.journeyCompletedTime);
        return null;
      };

      if (stepKey === "Completed") {
        if (reached.Completed) return timeFor("Completed") ?? "Completed";
        return eta;
      }
      if (stepIndex < furthest) {
        const t = timeFor(stepKey);
        return t ?? "Completed";
      }
      if (stepKey === "ReadyForPickup") return "In Progress";
      if (stepIndex === furthest) {
        const ownEvidence =
          (stepKey === "Order" && isPresent(shipmentCreationDate)) ||
          (stepKey === "AwaitingPickup" &&
            (isPresent(da.awaitingPickup) ||
              isPresent(da.awaitingPickupTime))) ||
          (stepKey === "InTransit" &&
            (isPresent(da.journeyStarted) || isPresent(da.journeyStartTime))) ||
          (stepKey === "Completed" &&
            (isPresent(da.journeyCompleted) ||
              isPresent(da.journeyCompletedTime))) ||
          (stepKey === "Order" && furthest > 0);
        if (ownEvidence) {
          const t = timeFor(stepKey);
          return t ?? "Completed";
        }
        return "In Progress";
      }
      return "Pending";
    },
    [shipment],
  );

  /*
   * Schedule metric for the two steps that carry a planned time:
   *   • "Picked Up" (AwaitingPickup) → planned pickup.date/time  vs actual awaitingPickupTime
   *   • "Completed"                  → planned delivery.date/time vs actual journeyCompletedTime
   *
   * Returns null when there is no planned time to compare against, so the
   * timeline stays clean for shipments without a schedule. When the step has
   * been reached we compare actual-vs-planned; when it hasn't been reached yet
   * but the planned moment has already passed, we surface an "at risk"
   * forecast (`fc`) based on how overdue it is right now.
   */
  const getStepMetric = useCallback(
    (stepKey: string) => {
      if (!shipment) return null;

      let plannedRaw: unknown;
      let plannedTime: string | undefined;
      let actualRaw: string | null | undefined;
      const da = shipment.driverAccess || ({} as typeof shipment.driverAccess);

      if (stepKey === "AwaitingPickup") {
        plannedRaw = shipment.pickup?.date;
        plannedTime = shipment.pickup?.time;
        actualRaw = da?.awaitingPickupTime;
      } else if (stepKey === "Completed") {
        plannedRaw = shipment.delivery?.date;
        plannedTime = shipment.delivery?.time;
        actualRaw = da?.journeyCompletedTime;
      } else {
        return null;
      }

      // Combine the stored date with the "HH:mm" planned time. No planned
      // date → nothing to measure against.
      const planned = safeDate(plannedRaw);
      if (!planned) return null;
      if (plannedTime && /^\d{1,2}:\d{2}/.test(plannedTime)) {
        const [h, m] = plannedTime.split(":").map(Number);
        planned.setHours(h, m, 0, 0);
      }

      const fmtDur = (mins: number) => {
        const abs = Math.abs(mins);
        const h = Math.floor(abs / 60);
        const m = abs % 60;
        if (h && m) return `${h}h ${m}m`;
        if (h) return `${h}h`;
        return `${m}m`;
      };

      const TOL = 5; // minutes of slack that still counts as "on time"
      const plannedLabel = format(planned, "d/M HH:mm");
      const actual = actualRaw ? safeDate(actualRaw) : null;

      if (actual) {
        const deltaMin = Math.round(
          (actual.getTime() - planned.getTime()) / 60000,
        );
        const tone =
          deltaMin > TOL ? "late" : deltaMin < -TOL ? "early" : "onTime";
        const badge =
          tone === "late"
            ? `+${fmtDur(deltaMin)} late`
            : tone === "early"
              ? `${fmtDur(deltaMin)} early`
              : "on time";
        return {
          plannedLabel,
          actualLabel: format(actual, "d/M HH:mm"),
          tone,
          badge,
        };
      }

      // Not reached yet. Only warn once we're actually past the planned time,
      // and only while this step is still current or upcoming (a step the
      // shipment has already moved past with no timestamp is missing data, not
      // "at risk").
      const stepIndex = STATUS_STEPS.findIndex((s) => s.key === stepKey);
      const curIndex = STATUS_STEPS.findIndex(
        (s) => s.key === shipment.status,
      );
      if (curIndex > stepIndex) {
        return { plannedLabel, actualLabel: null, tone: "onTime", badge: null };
      }

      // As soon as the planned moment has passed and the step still hasn't
      // happened, flag it — no grace window here, the schedule is already
      // missed. "fc" = forecast delay based on how overdue it is right now.
      const overdueMin = Math.round((Date.now() - planned.getTime()) / 60000);
      if (overdueMin > 0) {
        return {
          plannedLabel,
          actualLabel: null,
          tone: "atRisk",
          badge: `+${fmtDur(overdueMin)} fc`,
        };
      }

      return { plannedLabel, actualLabel: null, tone: "onTrack", badge: null };
    },
    [shipment],
  );

  // Tailwind classes for the right-side schedule badge, keyed by tone.
  const metricBadgeClass = (tone: string) =>
    tone === "late"
      ? "bg-red-50 text-red-600 border border-red-200"
      : tone === "atRisk"
        ? "bg-amber-50 text-amber-700 border border-amber-200"
        : "bg-green-50 text-green-600 border border-green-200";

  // Colour for the "Actual" value line — red when late, green otherwise.
  const metricActualClass = (tone: string) =>
    tone === "late" ? "text-red-600" : "text-green-600";

  // Sum stored per-row ldm / volumeM3 (which may be auto-filled from L/W/H
  // or manually entered). Don't recompute from dimensions — that would
  // silently throw away values the user typed in the form.
  const totalLDM = useMemo(
    () =>
      (shipment.cargoItems ?? []).reduce((sum, item) => {
        const ldm = Number(item.ldm);
        return Number.isFinite(ldm) && ldm > 0 ? sum + ldm : sum;
      }, 0),
    [shipment.cargoItems],
  );

  const totalVolume = useMemo(
    () =>
      (shipment.cargoItems ?? []).reduce((sum, item) => {
        const v = Number(item.volumeM3);
        return Number.isFinite(v) && v > 0 ? sum + v : sum;
      }, 0),
    [shipment.cargoItems],
  );

  const servicesGrandTotal = useMemo(
    () =>
      (shipment.services ?? []).reduce(
        (acc, item) => acc + item.price * item.qty,
        0,
      ),
    [shipment.services],
  );

  const costServicesGrandTotal = useMemo(
    () =>
      (shipment.costServices ?? []).reduce(
        (acc, item) => acc + item.price * item.qty,
        0,
      ),
    [shipment.costServices],
  );

  // Tax is only surfaced when at least one row carries a VAT category.
  const servicesShowTax = useMemo(
    () => listHasTax(shipment.services ?? []),
    [shipment.services],
  );
  const servicesTaxTotal = useMemo(
    () => sumTax(shipment.services ?? []),
    [shipment.services],
  );
  const costServicesShowTax = useMemo(
    () => listHasTax(shipment.costServices ?? []),
    [shipment.costServices],
  );
  const costServicesTaxTotal = useMemo(
    () => sumTax(shipment.costServices ?? []),
    [shipment.costServices],
  );

  const currentStepIndex = getCurrentStepIndex();

  /* ─────────────────────────── Timeline (shared JSX) ───────────────────── */
  const timelineContent = (
    <div className="relative">
      {STATUS_STEPS.map((step, index) => {
        const isCompleted =
          index < currentStepIndex ||
          (shipment?.status === "Completed" && index === currentStepIndex);
        const isCurrent =
          index === currentStepIndex && shipment?.status !== "Completed";
        const isLast = index === STATUS_STEPS.length - 1;
        const stepStatus = getStepStatus(step.key);
        const metric = getStepMetric(step.key);

        return (
          <div key={step.key} className="relative flex gap-3 pb-5 last:pb-0">
            {/* Connector */}
            {!isLast && (
              <div
                className={`absolute left-[11px] top-6 w-0.5 h-[calc(100%-10px)] ${
                  isCompleted ? "bg-green-400" : "bg-gray-200"
                }`}
              />
            )}
            {/* Circle */}
            <div
              className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-xs font-semibold ${
                isCompleted
                  ? "bg-green-500 text-white"
                  : isCurrent
                    ? "bg-primary text-white"
                    : "bg-gray-200 text-gray-500"
              }`}
            >
              {isCompleted ? (
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <span>{index + 1}</span>
              )}
            </div>
            {/* Label + date */}
            <div className="flex-1 min-w-0 pt-0.5">
              {/* Label and badge share a wrapping row: the label never breaks
                  mid-word, and the badge sits right after it, dropping to its
                  own (left-aligned) line when the sidebar is too narrow. */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <p
                  className={`text-sm font-medium leading-tight whitespace-nowrap ${
                    isCompleted || isCurrent ? "text-dark-900" : "text-gray-500"
                  }`}
                >
                  {step.label}
                </p>
                {metric?.badge && (
                  <span
                    className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold leading-none whitespace-nowrap ${metricBadgeClass(
                      metric.tone,
                    )}`}
                    title={
                      metric.tone === "atRisk"
                        ? "At risk — running late vs planned time"
                        : "Actual vs planned time"
                    }
                  >
                    {metric.tone === "atRisk" && (
                      <AlertTriangle className="w-3 h-3 mr-1" />
                    )}
                    {metric.badge}
                  </span>
                )}
              </div>
              {/* Hide the plain status time when the metric already shows the
                  actual timestamp on its "Actual:" line — otherwise it repeats. */}
              {stepStatus && !metric?.actualLabel && (
                <p
                  className={`text-xs mt-0.5 ${
                    isCompleted
                      ? "text-green-600"
                      : isCurrent
                        ? "text-primary"
                        : "text-gray-400"
                  }`}
                >
                  {stepStatus}
                </p>
              )}
              {metric && (
                <div className="mt-1 space-y-0.5 text-[11px] leading-tight">
                  <p className="text-gray-400">
                    Planned:{" "}
                    <span className="text-gray-600 font-medium">
                      {metric.plannedLabel}
                    </span>
                  </p>
                  {metric.actualLabel && (
                    <p className="text-gray-400">
                      Actual:{" "}
                      <span
                        className={`font-medium ${metricActualClass(
                          metric.tone,
                        )}`}
                      >
                        {metric.actualLabel}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  /* ─────────────────────────── render ──────────────────────────────────── */

  return (
    <div className="space-y-6 ">
      <div className="p-6 py-2 pb-10 max-w-[1440px] mx-auto w-full">
        <div className="flex gap-6">
          {/* ── MAIN CONTENT COLUMN ──────────────────────────────────────── */}
          <div className="flex-1 min-w-0 space-y-4 xl:border-r border-r-gray-200 xl:pr-6">
            {/* ── Header card ────────────────────────────────────────────── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-xl font-bold text-dark-900">
                    {shipment.shipmentNumber}
                  </h2>
                  {shipment.referenceNumber && (
                    <span className=" text-base font-normal">
                      (Ref: {shipment.referenceNumber})
                    </span>
                  )}
                  {(shipment as unknown as { pohSummary?: any }).pohSummary &&
                    shipment.pohLevel !== 0 && (
                      <POHStatusDots
                        poh={
                          (shipment as unknown as { pohSummary: any })
                            .pohSummary
                        }
                        pohLevel={shipment.pohLevel}
                        showLabel
                      />
                    )}
                  {shipment.linkedTaskId &&
                    (() => {
                      const lt = shipment.linkedTaskId;
                      const taskNum =
                        typeof lt === "object" ? lt.taskNumber : lt;
                      const taskDesc =
                        typeof lt === "object" ? lt.description : undefined;
                      return (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                          title={taskDesc}
                        >
                          {taskNum}
                        </span>
                      );
                    })()}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={stopPropagation(() =>
                      handleShareShipment(shipment),
                    )}
                    className="p-1  hover:text-primary-600 transition-colors"
                    title={t("readonly.share")}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>
                  {shipment.type && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded">
                      {shipment.type === "Domestic" ? (
                        <Home className="w-3.5 h-3.5" />
                      ) : (
                        <Globe className="w-3.5 h-3.5" />
                      )}

                      {shipment.type === "International"
                        ? t("readonly.internationalCmr")
                        : shipment.type === "Domestic"
                          ? t("readonly.domesticInland")
                          : t("readonly.workOrder")}
                    </span>
                  )}
                  {[0, 1, 2].includes(shipment.pohLevel) && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded bg-amber-50 text-amber-700 border border-amber-200">
                      POH L{shipment.pohLevel}
                    </span>
                  )}

                  {shipment.direction && (
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded ${
                        shipment.direction === "Inbound"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {shipment.direction}
                      {shipment.direction === "Outbound" && (
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      )}
                      {shipment.direction === "Inbound" && (
                        <svg
                          className="w-3.5 h-3.5 order-first"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11 17l-5-5m0 0l5-5m-5 5h12"
                          />
                        </svg>
                      )}
                    </span>
                  )}

                  <div className="relative xl:hidden">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded cursor-help transition-all hover:shadow-md ${getStatusColor(shipment?.status)}`}
                      onMouseEnter={() => setIsStatusHovered(true)}
                      onMouseLeave={() => setIsStatusHovered(false)}
                    >
                      {STATUS_STEPS.find((s) => s.key === shipment?.status)
                        ?.label || shipment?.status}
                      <svg
                        className="w-3.5 h-3.5 opacity-60"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </span>

                    {/* Hover popup */}
                    {isStatusHovered && (
                      <div className="absolute top-full mt-2 right-0 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[320px]">
                        <div className="space-y-3">
                          {STATUS_STEPS.map((step, index) => {
                            const isCompleted =
                              index < currentStepIndex ||
                              (shipment?.status === "Completed" &&
                                index === currentStepIndex);
                            const isCurrent =
                              index === currentStepIndex &&
                              shipment?.status !== "Completed";
                            const stepStatus = getStepStatus(step.key);
                            const metric = getStepMetric(step.key);
                            return (
                              <div
                                key={step.key}
                                className="flex items-start gap-3"
                              >
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 ${
                                    isCompleted
                                      ? "bg-green-500 text-white"
                                      : isCurrent
                                        ? "bg-primary text-white"
                                        : "bg-gray-200 text-gray-500"
                                  }`}
                                >
                                  {isCompleted ? (
                                    <svg
                                      className="w-3 h-3"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    <span>{index + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <div
                                      className={`font-medium text-sm whitespace-nowrap ${isCompleted || isCurrent ? "text-dark-900" : "text-gray-500"}`}
                                    >
                                      {step.label}
                                    </div>
                                    {metric?.badge && (
                                      <span
                                        className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold leading-none whitespace-nowrap ${metricBadgeClass(
                                          metric.tone,
                                        )}`}
                                      >
                                        {metric.tone === "atRisk" && (
                                          <AlertTriangle className="w-3 h-3 mr-1" />
                                        )}
                                        {metric.badge}
                                      </span>
                                    )}
                                  </div>
                                  {stepStatus && !metric?.actualLabel && (
                                    <div
                                      className={`text-xs mt-0.5 ${isCompleted ? "text-green-600" : isCurrent ? "text-primary" : "text-gray-400"}`}
                                    >
                                      {stepStatus}
                                    </div>
                                  )}
                                  {metric && (
                                    <div className="mt-1 space-y-0.5 text-[11px] leading-tight">
                                      <div className="text-gray-400">
                                        Planned:{" "}
                                        <span className="text-gray-600 font-medium">
                                          {metric.plannedLabel}
                                        </span>
                                      </div>
                                      {metric.actualLabel && (
                                        <div className="text-gray-400">
                                          Actual:{" "}
                                          <span
                                            className={`font-medium ${metricActualClass(
                                              metric.tone,
                                            )}`}
                                          >
                                            {metric.actualLabel}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {/* ── LEFT COLUMN: Client → Pickup → Delivery ─────────────── */}
              <div className="space-y-4">
                {/* Client */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-dark-900 mb-4">
                    {t("readonly.client")}
                  </h3>
                  <p className="text-dark-900 font-medium">
                    {shipment?.client?.companyName ||
                      t("readonly.notSpecified")}
                    {shipment?.client?.companyName &&
                      shipment?.client?.clientCode && (
                        <span className="text-gray-600 font-normal ml-2">
                          ({shipment.client.clientCode})
                        </span>
                      )}
                  </p>
                  {(shipment?.client?.address?.street ||
                    shipment?.client?.address?.city ||
                    shipment?.client?.address?.country) && (
                    <p className=" mt-1 text-sm">
                      {formatAddress(
                        shipment?.client?.address?.street?.split(",")[0],
                        shipment?.client?.address?.city,
                        shipment?.client?.address?.state,
                        shipment?.client?.address?.postalCode,
                        shipment?.client?.address?.country,
                      )}
                    </p>
                  )}
                </div>

                {/* Pickup */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-green-600" />
                      <h3 className="text-lg font-semibold text-dark-900">
                        {t("readonly.pickup")}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5  text-sm">
                      <Clock className="h-4 w-4" />
                      <span>
                        {shipment?.pickup?.date
                          ? new Date(shipment.pickup.date).toLocaleDateString()
                          : t("readonly.noDate")}{" "}
                        / {shipment?.pickup?.time || t("readonly.noTime")}
                      </span>
                    </div>
                  </div>
                  <p className="text-dark-900 font-medium">
                    {shipment?.pickup?.companyName ||
                      t("readonly.notSpecified")}
                  </p>
                  {(shipment?.pickup?.address?.street ||
                    shipment?.pickup?.address?.city ||
                    shipment?.pickup?.address?.country) && (
                    <p className=" mt-1 text-sm">
                      {formatAddress(
                        shipment?.pickup?.address?.street?.split(",")[0],
                        shipment?.pickup?.address?.city,
                        shipment?.pickup?.address?.state,
                        shipment?.pickup?.address?.postalCode,
                        shipment?.pickup?.address?.country,
                      )}
                    </p>
                  )}
                </div>

                {/* Delivery */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-red-500" />
                      <h3 className="text-lg font-semibold text-dark-900">
                        {t("readonly.delivery")}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5  text-sm">
                      <Clock className="h-4 w-4" />
                      <span>
                        {shipment?.delivery?.date
                          ? new Date(
                              shipment.delivery.date,
                            ).toLocaleDateString()
                          : t("readonly.noDate")}{" "}
                        / {shipment?.delivery?.time || t("readonly.noTime")}
                      </span>
                    </div>
                  </div>
                  <p className="text-dark-900 font-medium">
                    {shipment?.delivery?.companyName ||
                      t("readonly.notSpecified")}
                    {shipment?.delivery?.companyName &&
                      shipment?.delivery?.registerationCode && (
                        <span className=" font-normal ml-2">
                          ({shipment.delivery.registerationCode})
                        </span>
                      )}
                  </p>
                  {(shipment?.delivery?.address?.street ||
                    shipment?.delivery?.address?.city ||
                    shipment?.delivery?.address?.country) && (
                    <p className=" mt-1 text-sm">
                      {formatAddress(
                        shipment?.delivery?.address?.street?.split(",")[0],
                        shipment?.delivery?.address?.city,
                        shipment?.delivery?.address?.state,
                        shipment?.delivery?.address?.postalCode,
                        shipment?.delivery?.address?.country,
                      )}
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div>
                    <h3 className="text-lg font-semibold text-dark-900 mb-3">
                      {t("readonly.remarks")}
                    </h3>
                    <p className="text-dark-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {shipment?.additionalNotes || "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN: (SEA: Sea Freight →) Transport → Remarks ── */}
              <div className="space-y-4">
                {/* Sea Freight Details — SEA only, shown before Transport.
                    Always rendered (fields fall back to "-") and includes the
                    forwarder block. */}
                {isSea && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Ship className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-dark-900">
                        {t("sea.title")}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <span className="text-dark-500">
                          {t("sea.shippingLine")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.shippingLine || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.vesselName")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.vesselName || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.imoNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.imoNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.voyageNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.voyageNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.bookingNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.bookingNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.portOfLoading")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.portOfLoading || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.portOfDischarge")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.portOfDischarge || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.placeOfReceipt")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.placeOfReceipt || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.placeOfDelivery")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {placeOfDeliveryDisplay}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.containerNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.carrier?.containerNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.containerType")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {containerTypeLabel}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.sealNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.sealNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.masterBLNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.masterBLNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.blNumber")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.blNumber || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.blDate")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.blDate
                            ? new Date(
                                shipment.seaDetails.blDate,
                              ).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.blType")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {blTypeLabel}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.numberOfOriginals")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.numberOfOriginals ?? "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">
                          {t("sea.freightPaymentTerms")}:{" "}
                        </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.freightPaymentTerms || "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">{t("sea.etd")}: </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.etd
                            ? new Date(
                                shipment.seaDetails.etd,
                              ).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                      <div>
                        <span className="text-dark-500">{t("sea.eta")}: </span>
                        <span className="text-dark-900 font-medium">
                          {shipment.seaDetails?.eta
                            ? new Date(
                                shipment.seaDetails.eta,
                              ).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                    </div>

                    {/* B/L Remarks */}
                    {shipment.seaDetails?.blRemarks &&
                      shipment.seaDetails.blRemarks.length > 0 && (
                        <div className="border-t border-gray-100 pt-3 mt-3">
                          <h4 className="text-sm font-semibold text-dark-900 mb-2">
                            {t("blRemarks.title")}
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-dark-900">
                            {shipment.seaDetails.blRemarks
                              .filter((r) => r?.text && r.text.trim() !== "")
                              .map((r, i) => {
                                const locationLabel =
                                  BL_REMARK_LOCATION_OPTIONS.find(
                                    (o) => o.value === r.location,
                                  )?.label;
                                return (
                                  <li key={i}>
                                    {locationLabel && (
                                      <span className="text-dark-500">
                                        {locationLabel}:{" "}
                                      </span>
                                    )}
                                    {r.text}
                                  </li>
                                );
                              })}
                          </ul>
                        </div>
                      )}

                    {/* Forwarder Information — always shown for SEA in read-only */}
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <h4 className="text-sm font-semibold text-dark-900 mb-2">
                        {t("forwarder.title")}
                      </h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <span className="text-dark-500">
                            {t("forwarder.companyName")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.companyName || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("forwarder.contactPerson")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.contactPerson ||
                              "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("labels.email")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.email || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("labels.phone")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.phone || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("forwarder.houseBLNumber")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.houseBLNumber ||
                              "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("forwarder.reference")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.forwarder?.reference || "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Notify Party — always shown for SEA in read-only */}
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <h4 className="text-sm font-semibold text-dark-900 mb-2">
                        {t("notifyParty.title")}
                      </h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <span className="text-dark-500">
                            {t("notifyParty.company")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.notifyParty?.company || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("notifyParty.contact")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.notifyParty?.contact || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("labels.email")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.notifyParty?.email || "-"}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">
                            {t("labels.phone")}:{" "}
                          </span>
                          <span className="text-dark-900 font-medium">
                            {shipment.seaDetails?.notifyParty?.phone || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transport */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-dark-900 mb-4">
                    {t("readonly.transport")}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          {t("readonly.transportMode")}
                        </label>
                        <p className="text-dark-900">
                          {transportMode === "SEA"
                            ? t("transportMode.sea")
                            : transportMode === "AIR"
                              ? t("transportMode.air")
                              : t("transportMode.road")}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          {t("readonly.loadType")}
                        </label>
                        <p className="text-dark-900">{loadTypeLabel || "-"}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          {t("readonly.incoterm")}
                        </label>
                        <p className="text-dark-900">
                          {shipment.incoterms
                            ? `${shipment.incoterms}${shipment.place ? ` / ${shipment.place}` : ""}`
                            : "-"}
                        </p>
                      </div>
                    </div>

                    <p className="text-dark-900 font-semibold">
                      {shipment?.carrier?.companyName ||
                        t("readonly.notSpecified")}
                    </p>

                    <div className="grid grid-cols-3 gap-4">
                      {isRoad && (
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-1">
                            {t("readonly.truckTrailer")}
                          </label>
                          <p className="text-dark-900 text-sm font-medium">
                            {[
                              vehicleRefLabel(shipment?.carrier?.truckLicensePlate),
                              vehicleRefLabel(shipment?.carrier?.trailerLicensePlate),
                            ]
                              .filter(Boolean)
                              .join(" / ") || "-"}
                          </p>
                        </div>
                      )}
                      {isRoad && (
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-1">
                            {t("readonly.container")}
                          </label>
                          <p className="text-dark-900 text-sm font-medium">
                            {shipment?.carrier?.containerNumber || "-"}
                          </p>
                        </div>
                      )}
                      {isRoad && (
                        <div>
                          <label className="block text-sm font-medium text-dark-700 mb-1">
                            {t("readonly.driver")}
                          </label>
                          <p className="text-dark-900 text-sm font-medium">
                            {typeof shipment?.assignedDriver === "object" &&
                            shipment?.assignedDriver !== null
                              ? `${shipment.assignedDriver.firstName ?? ""} ${shipment.assignedDriver.lastName ?? ""}`.trim() ||
                                "-"
                              : shipment?.assignedDriver || "-"}
                          </p>
                        </div>
                      )}
                    </div>

                    {transportMode === "AIR" && shipment.airDetails && (
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Plane className="h-4 w-4 text-purple-600" />
                          <h4 className="text-sm font-semibold text-dark-900">
                            {t("air.title")}
                          </h4>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <span className="text-dark-500">
                              {t("air.airlineName")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.airlineName || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.flightNumber")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.flightNumber || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.airportOfDeparture")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.airportOfDeparture || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.airportOfArrival")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.airportOfArrival || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.mawbNumber")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.mawbNumber || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.uldNumber")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.uldNumber || "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.etd")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.etd
                                ? new Date(
                                    shipment.airDetails.etd,
                                  ).toLocaleDateString()
                                : "-"}
                            </span>
                          </div>
                          <div>
                            <span className="text-dark-500">
                              {t("air.eta")}:{" "}
                            </span>
                            <span className="text-dark-900 font-medium">
                              {shipment.airDetails.eta
                                ? new Date(
                                    shipment.airDetails.eta,
                                  ).toLocaleDateString()
                                : "-"}
                            </span>
                          </div>
                          {shipment.airDetails.forwarder?.companyName && (
                            <>
                              <div>
                                <span className="text-dark-500">
                                  {t("forwarder.companyName")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.forwarder.companyName}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("forwarder.hawbNumber")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.forwarder.hawbNumber ||
                                    "-"}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* AWB document block */}
                        {shipment.airDetails.awb && (
                          <div className="border-t border-gray-100 pt-3 mt-3">
                            <h5 className="text-xs font-semibold text-dark-900 mb-2 uppercase tracking-wide">
                              {t("awb.title")}
                            </h5>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.awbNumber")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.awbNumber || "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.awbDate")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.awbDate
                                    ? new Date(
                                        shipment.airDetails.awb.awbDate,
                                      ).toLocaleDateString()
                                    : "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.issuingAgentName")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.issuingAgentName ||
                                    "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.issuingAgentCity")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.issuingAgentCity ||
                                    "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.freightPaymentTerms")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.freightPaymentTerms ||
                                    "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.otherChargesPayment")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.otherChargesPayment ||
                                    "-"}
                                </span>
                              </div>
                              <div>
                                <span className="text-dark-500">
                                  {t("awb.currency")}:{" "}
                                </span>
                                <span className="text-dark-900 font-medium">
                                  {shipment.airDetails.awb.currency || "-"}
                                </span>
                              </div>
                              {(shipment.airDetails.awb.specialHandlingCodes
                                ?.length ?? 0) > 0 && (
                                <div>
                                  <span className="text-dark-500">
                                    {t("awb.specialHandlingCodes")}:{" "}
                                  </span>
                                  <span className="text-dark-900 font-medium">
                                    {shipment.airDetails.awb.specialHandlingCodes
                                      ?.filter(Boolean)
                                      .join(", ")}
                                  </span>
                                </div>
                              )}
                              {shipment.airDetails.awb.notifyParty
                                ?.companyName && (
                                <div className="col-span-2">
                                  <span className="text-dark-500">
                                    {t("notifyParty.title")}:{" "}
                                  </span>
                                  <span className="text-dark-900 font-medium">
                                    {[
                                      shipment.airDetails.awb.notifyParty
                                        .companyName,
                                      shipment.airDetails.awb.notifyParty
                                        .contactPerson,
                                      shipment.airDetails.awb.notifyParty.email,
                                      shipment.airDetails.awb.notifyParty.phone,
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {(shipment?.cargoHandling?.temperatureSensitive ||
                      shipment?.cargoHandling?.adr ||
                      shipment?.cargoHandling?.fragile) && (
                      <div className="flex flex-wrap gap-2">
                        {shipment.cargoHandling.temperatureSensitive && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                            <Thermometer className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {shipment.cargoHandling.temperatureRange ||
                                t("readonly.tempSensitive")}
                            </span>
                          </div>
                        )}
                        {shipment.cargoHandling.adr && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200">
                            <ShieldAlert className="h-4 w-4 text-red-600" />
                            <span className="text-sm font-medium text-red-900">
                              {shipment.cargoHandling.imoClass ||
                                t("readonly.adr")}
                            </span>
                          </div>
                        )}
                        {shipment.cargoHandling.fragile && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-200">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-900">
                              {t("readonly.handleWithCare")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Cargo ──────────────────────────────────────────────────── */}
            <div>
              <h3 className="text-lg font-semibold text-dark-900 mb-4">
                {t("readonly.cargoInfo")}
              </h3>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {[
                          t("readonly.quantity"),
                          t("readonly.type"),
                          t("readonly.weightKg"),
                          t("readonly.volumeM3"),
                          t("readonly.dimensionsLwh"),
                          // LDM is ROAD-only; chargeableWeight is AIR-only.
                          ...(isRoad ? [t("readonly.ldm")] : []),
                          ...(isAir ? [t("readonly.chargeableWeight")] : []),
                          t("readonly.description"),
                          t("readonly.marksNumbers"),
                        ].map((header) => (
                          <th
                            key={header}
                            className="px-4 py-3 text-left text-xs font-medium text-dark-500 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shipment?.cargoItems?.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {displayCargoNumber(item.quantity)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {item.type || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {displayCargoNumber(item.weightKg)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {displayCargoNumber(item.volumeM3)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {displayCargoNumber(item.length)} ×{" "}
                            {displayCargoNumber(item.width)} ×{" "}
                            {displayCargoNumber(item.height)}
                          </td>
                          {isRoad && (
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                              {displayCargoNumber(item.ldm)}
                            </td>
                          )}
                          {isAir && (
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                              {displayCargoNumber(item.chargeableWeight)}
                            </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {item.description || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-dark-900">
                            {item.marks || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {shipment?.cargoItems && shipment.cargoItems.length > 0 && (
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-dark-700">
                        {t("readonly.totals")}
                      </span>
                      <div className="flex gap-8">
                        <div className="text-sm">
                          <span className="font-medium text-dark-500">
                            {t("readonly.quantityLabel")}
                          </span>{" "}
                          <span className="font-semibold text-dark-900">
                            {shipment?.totalQuantity || 0}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-dark-500">
                            {t("readonly.weightLabel")}
                          </span>{" "}
                          <span className="font-semibold text-dark-900">
                            {shipment?.totalWeight?.toFixed(2) || 0} kg
                          </span>
                        </div>
                        {isRoad && (
                          <div className="text-sm">
                            <span className="font-medium text-dark-500">
                              {t("readonly.ldmLabel")}
                            </span>{" "}
                            <span className="font-semibold text-dark-900">
                              {totalLDM.toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="text-sm">
                          <span className="font-medium text-dark-500">
                            {t("readonly.volLabel")}
                          </span>{" "}
                          <span className="font-semibold text-dark-900">
                            {totalVolume.toFixed(3)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(!shipment?.cargoItems ||
                  shipment.cargoItems.length === 0) && (
                  <div className="text-center py-8">
                    <p className="text-dark-500">{t("readonly.noCargo")}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Pricing ──────── */}
            <div>
              <button
                onClick={() => setIsPricingOpen(!isPricingOpen)}
                className="w-full flex items-center justify-between text-lg font-semibold text-gray-900 hover:text-gray-700 transition-colors"
              >
                <span>{t("readonly.pricing")}</span>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${isPricingOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isPricingOpen
                    ? "max-h-96 opacity-100 mt-4"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            t("readonly.totalBuyingPrice"),
                            t("readonly.totalSellingPrice"),
                            // Price/km + mileage are ROAD-only.
                            ...(isRoad
                              ? [
                                  t("readonly.pricePerKm"),
                                  t("readonly.mileageKm"),
                                ]
                              : []),
                            t("readonly.currency"),
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {shipment.pricing?.totalBuyingPrice || "0.00"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {shipment.pricing?.totalSellingPrice || "0.00"}
                          </td>
                          {isRoad && (
                            <>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {shipment.pricing?.pricePerKm || "0.00"}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {shipment.pricing?.mileage || "0.00"}
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {shipment.pricing?.currency} (
                            {shipment.pricing?.currency === "EUR"
                              ? "€"
                              : shipment.pricing?.currency === "USD"
                                ? "$"
                                : shipment.pricing?.currency === "GBP"
                                  ? "£"
                                  : shipment.pricing?.currency}
                            )
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Additional Services ──────────────────────────────────── */}
            <div>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="w-full flex items-center justify-between text-lg font-semibold text-gray-900 hover:text-gray-700 transition-colors"
              >
                <span>{t("readonly.additionalServices")}</span>
                <div className="flex items-center gap-2">
                  {/* Summary badge when collapsed */}
                  {!isServicesOpen &&
                    shipment?.services &&
                    shipment.services.length > 0 && (
                      <span className="text-sm font-normal text-gray-500">
                        {shipment.services.length}{" "}
                        {shipment.services.length === 1
                          ? "service"
                          : "services"}{" "}
                        · €{servicesGrandTotal.toFixed(2)}
                      </span>
                    )}
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isServicesOpen
                    ? "max-h-[2000px] opacity-100 mt-4"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            t("readonly.by"),
                            t("readonly.serviceCode"),
                            t("readonly.description"),
                            t("readonly.unitType"),
                            t("readonly.quantity"),
                            t("readonly.price"),
                            t("readonly.tax"),
                            t("readonly.totalExcludingTax"),
                            t("services.invoicedColumn"),
                            ...(canManageServices
                              ? [t("services.action")]
                              : []),
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-medium text-dark-500 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {shipment?.services?.map((field, index) => {
                          const isDriver =
                            field.addedBy?.toLowerCase() === "driver";
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-2 py-3 whitespace-nowrap w-[100px]">
                                <div
                                  className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-xs font-semibold ${
                                    isDriver
                                      ? "bg-blue-600 text-white"
                                      : "bg-green-600 text-white"
                                  }`}
                                >
                                  {isDriver ? "D" : "SU"}
                                </div>
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap w-[250px] text-sm text-dark-900 font-medium">
                                {field.name}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500 border border-gray-200 max-w-[450px]">
                                <p
                                  className="truncate"
                                  title={field.description || "-"}
                                >
                                  {field.description || "-"}
                                </p>
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {field.type}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {field.qty}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                €{field.price}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {taxCellLabel(
                                  field.price * field.qty,
                                  field.tax,
                                )}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap w-[150px] text-sm font-medium text-dark-900">
                                €{(field.price * field.qty).toFixed(2)}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap">
                                {field.invoiceId ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                                      {t("services.invoicedBadge")}
                                    </span>
                                    {invoiceNumberById[field.invoiceId] && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          navigate(
                                            `/invoices/${field.invoiceId}`,
                                          )
                                        }
                                        title={t("services.viewInvoice")}
                                        className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer"
                                      >
                                        {invoiceNumberById[field.invoiceId]}
                                        <ExternalLink className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">
                                    {t("services.uninvoicedBadge")}
                                  </span>
                                )}
                              </td>
                              {canManageServices && (
                                <td className="px-2 py-3 whitespace-nowrap text-sm">
                                  {field.invoiceId ? (
                                    <span
                                      className="inline-flex items-center justify-center p-1 text-gray-400"
                                      title={t("services.lockedInvoiced")}
                                    >
                                      <Lock className="w-4 h-4" />
                                    </span>
                                  ) : field._id ? (
                                    <button
                                      type="button"
                                      disabled={serviceBusy}
                                      onClick={() =>
                                        handleDeleteService(field._id!)
                                      }
                                      className="text-red-600 hover:text-red-700 disabled:opacity-50 p-1"
                                      title={t("services.deleteService")}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  ) : null}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {shipment?.services && shipment.services.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-dark-700">
                          {t("readonly.totals")}
                        </span>
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <span className="font-medium text-dark-500">
                              {t("readonly.totalPrice")}
                            </span>{" "}
                            <span className="font-semibold text-dark-900">
                              €{servicesGrandTotal.toFixed(2)}
                            </span>
                          </div>
                          {servicesShowTax && (
                            <>
                              <div>
                                <span className="font-medium text-dark-500">
                                  {t("readonly.taxTotal")}
                                </span>{" "}
                                <span className="font-semibold text-dark-900">
                                  €{servicesTaxTotal.toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-dark-500">
                                  {t("readonly.totalWithTax")}
                                </span>{" "}
                                <span className="font-semibold text-dark-900">
                                  €
                                  {(
                                    servicesGrandTotal + servicesTaxTotal
                                  ).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {(!shipment?.services || shipment.services.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-dark-500">
                        {t("readonly.noServices")}
                      </p>
                    </div>
                  )}

                  {/* Post-completion service management */}
                  {canManageServices && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-white">
                      {!showAddService ? (
                        <button
                          type="button"
                          onClick={() => setShowAddService(true)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-700"
                        >
                          <Plus className="w-4 h-4" />
                          {t("services.addService")}
                        </button>
                      ) : (
                        <ServiceAddInline
                          services={serviceCatalog}
                          busy={serviceBusy}
                          onSave={handleAddService}
                          onCancel={() => setShowAddService(false)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Cost Services ────────────────────────────────────────── */}
            <div>
              <button
                onClick={() => setIsCostServicesOpen(!isCostServicesOpen)}
                className="w-full flex items-center justify-between text-lg font-semibold text-gray-900 hover:text-gray-700 transition-colors"
              >
                <span>{t("costServices.title")}</span>
                <div className="flex items-center gap-2">
                  {/* Summary badge when collapsed */}
                  {!isCostServicesOpen &&
                    shipment?.costServices &&
                    shipment.costServices.length > 0 && (
                      <span className="text-sm font-normal text-gray-500">
                        {shipment.costServices.length}{" "}
                        {shipment.costServices.length === 1
                          ? t("costServices.serviceCount_one")
                          : t("costServices.serviceCount_other")}{" "}
                        · €{costServicesGrandTotal.toFixed(2)}
                      </span>
                    )}
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${isCostServicesOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isCostServicesOpen
                    ? "max-h-[2000px] opacity-100 mt-4"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            // No "By": a cost row is always the office's. The
                            // only driver-written charges in the product are a
                            // shipment's *billable* services, which is why the
                            // table above this one still carries the column.
                            t("costServices.serviceCode"),
                            t("costServices.description"),
                            t("costServices.unitType"),
                            t("costServices.qty"),
                            t("costServices.price"),
                            t("costServices.tax"),
                            t("costServices.totalExcludingTax"),
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-medium text-dark-500 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {shipment?.costServices?.map((field, index) => {
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-2 py-3 whitespace-nowrap w-[250px] text-sm text-dark-900 font-medium">
                                {field.name}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500 border border-gray-200 max-w-[450px]">
                                <p
                                  className="truncate"
                                  title={field.description || "-"}
                                >
                                  {field.description || "-"}
                                </p>
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {field.type}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {field.qty}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                €{field.price}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-sm text-dark-900">
                                {taxCellLabel(
                                  field.price * field.qty,
                                  field.tax,
                                )}
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap w-[150px] text-sm font-medium text-dark-900">
                                €{(field.price * field.qty).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {shipment?.costServices &&
                    shipment.costServices.length > 0 && (
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-dark-700">
                            {t("costServices.totals")}
                          </span>
                          <div className="flex items-center gap-4 text-sm">
                            <div>
                              <span className="font-medium text-dark-500">
                                {t("costServices.totalPrice")}
                              </span>{" "}
                              <span className="font-semibold text-dark-900">
                                €{costServicesGrandTotal.toFixed(2)}
                              </span>
                            </div>
                            {costServicesShowTax && (
                              <>
                                <div>
                                  <span className="font-medium text-dark-500">
                                    {t("costServices.taxTotal")}
                                  </span>{" "}
                                  <span className="font-semibold text-dark-900">
                                    €{costServicesTaxTotal.toFixed(2)}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-dark-500">
                                    {t("costServices.totalWithTax")}
                                  </span>{" "}
                                  <span className="font-semibold text-dark-900">
                                    €
                                    {(
                                      costServicesGrandTotal +
                                      costServicesTaxTotal
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {(!shipment?.costServices ||
                    shipment.costServices.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-dark-500">
                        {t("readonly.noServices")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Files ────────────────────────────────────────────────── */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-dark-900 mb-4">
                {t("readonly.files")}
              </h3>

              {(shipment.driverAttachments &&
                shipment.driverAttachments.length > 0) ||
              (shipment.attachments && shipment.attachments.length > 0) ||
              (shipment.publicAttachments &&
                shipment.publicAttachments.length > 0) ? (
                <div className="space-y-6">
                  {/* POH Confirmation files */}
                  {shipment.driverAttachments &&
                    shipment.driverAttachments.filter(
                      (file) => file.attachmentType === "poh",
                    ).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-dark-700 mb-2">
                          {t("readonly.pohLabel")}
                        </h4>
                        <div className="space-y-2">
                          {shipment.driverAttachments
                            .filter((file) => file.attachmentType === "poh")
                            .map((file) => (
                              <div
                                key={file._id}
                                className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="shrink-0">
                                    <File className="h-5 w-5 text-gray-500" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-dark-900 truncate">
                                      {file.originalName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size)} •{" "}
                                      {new Date(
                                        file.uploadedAt,
                                      ).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => openAttachment(file)}
                                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                    title={t("readonly.openFile")}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadAttachment(file)}
                                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                                    title={t("readonly.downloadFile")}
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Driver files */}
                  {shipment.driverAttachments &&
                    shipment.driverAttachments.filter(
                      (file) => file.attachmentType !== "poh",
                    ).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-dark-700 mb-2">
                          {t("readonly.driverLabel")}
                        </h4>
                        <div className="space-y-2">
                          {shipment.driverAttachments
                            .filter((file) => file.attachmentType !== "poh")
                            .map((file) => (
                              <div
                                key={file._id}
                                className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="shrink-0">
                                    <File className="h-5 w-5 text-gray-500" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-dark-900 truncate">
                                      {file.originalName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size)} •{" "}
                                      {new Date(
                                        file.uploadedAt,
                                      ).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => openAttachment(file)}
                                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                    title={t("readonly.openFile")}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadAttachment(file)}
                                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                                    title={t("readonly.downloadFile")}
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Admin files */}
                  {shipment.attachments && shipment.attachments.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-dark-700 mb-2">
                        {t("readonly.adminLabel")}
                      </h4>
                      <div className="space-y-2">
                        {shipment.attachments.map((file) => (
                          <div
                            key={file._id}
                            className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="shrink-0">
                                <File className="h-5 w-5 text-gray-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-dark-900 truncate">
                                  {file.originalName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(file.size)} •{" "}
                                  {new Date(
                                    file.uploadedAt,
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => openAttachment(file)}
                                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                title={t("readonly.openFile")}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDownloadShipmentAttachment(
                                    file._id,
                                    false,
                                  )
                                }
                                className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                                title={t("readonly.downloadFile")}
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Client uploaded files */}
                  {shipment.publicAttachments &&
                    shipment.publicAttachments.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-dark-700 mb-2">
                          {t("publicView.clientLabel")}
                        </h4>
                        <div className="space-y-2">
                          {shipment.publicAttachments.map((file) => (
                            <div
                              key={file._id}
                              className="flex sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 gap-3"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="shrink-0">
                                  <File className="h-5 w-5 text-gray-500" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-dark-900 truncate">
                                    {file.originalName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(file.size)} •{" "}
                                    {new Date(
                                      file.uploadedAt,
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() =>
                                    window.open(file.fileUrl, "_blank")
                                  }
                                  className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                  title={t("readonly.openFile")}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <File className="h-12 w-12 text-dark-400 mx-auto" />
                  <h3 className="mt-2 text-sm font-medium text-dark-900">
                    {t("attachments.noAttachments")}
                  </h3>
                  <p className="mt-1 text-sm text-dark-500">
                    {t("readonly.noDriverAttachments")}
                  </p>
                </div>
              )}
            </div>

            {/* ── Comments ─────────────────────────────────────────────── */}
            {shipment?._id && (
              <CommentSection
                shipmentId={shipment._id}
                title={t("comments.titleToDriver")}
              />
            )}
            {shipment?._id && (
              <div className="bg-white rounded-md shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                  <span className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
                    Reclamations
                  </span>
                </div>
                <div className="px-4 py-3">
                  <ReclamationSection shipmentId={shipment._id} />
                </div>
              </div>
            )}

            {/* Invoices billed against this shipment. Lives in the left content
                column so it aligns with the sections above and leaves the
                timeline sidebar clear on the right. */}
            {shipment?._id && <InvoiceRefs shipmentId={shipment._id} />}
          </div>

          {/* ── TIMELINE SIDEBAR  ─────────────────────────────── */}
          <div className="hidden xl:block w-[150px] shrink-0">
            <div className="sticky top-4">
              <p className="text-xs font-semibold text-dark-500 uppercase tracking-wider mb-5 px-1">
                {t("readonly.timeline")}
              </p>
              {timelineContent}

              {/* ── Profitability summary (excluding tax) ─────────── */}
              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3">
                <div className="px-1">
                  <p className="text-[11px] leading-snug text-dark-500">
                    {t("readonly.invoicingExclTaxLabel")}
                  </p>
                  <p className="text-sm font-semibold text-dark-900">
                    €{servicesGrandTotal.toFixed(2)}
                  </p>
                </div>
                <div className="px-1">
                  <p className="text-[11px] leading-snug text-dark-500">
                    {t("readonly.costExclTaxLabel")}
                  </p>
                  <p className="text-sm font-semibold text-dark-900">
                    €{costServicesGrandTotal.toFixed(2)}
                  </p>
                </div>
                <div className="px-1">
                  <p className="text-[11px] leading-snug text-dark-500">
                    {t("readonly.netProfitExclTaxLabel")}
                  </p>
                  <p
                    className={`text-sm font-bold ${
                      servicesGrandTotal - costServicesGrandTotal < 0
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    €{(servicesGrandTotal - costServicesGrandTotal).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sharing modal */}
      {isSharingModalOpen && sharingDocumentId && (
        <CMRSharingModal
          key={sharingDocumentId._id + sharingDocumentId.waybill?.status}
          documentId={sharingDocumentId}
          isOpen={true}
          onClose={handleCloseSharingModal}
        />
      )}
    </div>
  );
};

export default ShipmentReadonly;
